import React, { useState } from 'react';
import { Currency } from '../types';
import { ExchangeRateWidget } from '../components/ExchangeRateWidget';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Input, Label, Select } from '../components/ui/Forms';
import { Button } from '../components/ui/Button';
import { InfoTooltip } from '../components/ui/InfoTooltip';
import { Check, Users, Flame, Cake, Globe, MapPin, Settings2, TrendingUp, SlidersHorizontal, ArrowLeftRight, AlertTriangle, Trash2 } from 'lucide-react';
import { Settings } from '../types';
import { CURRENCIES, COUNTRIES } from '../utils/countries';
interface SettingsPageProps {
  baseCurrency: Currency;
  onSetBaseCurrency: (currency: Currency) => void;
  partnerNames: [string, string];
  onSaveNames: (names: [string, string]) => void;
  settings: Settings;
  onUpdateSettings: (settings: Settings) => void;
  onResetAll: () => void;
}
export function SettingsPage({
  baseCurrency,
  onSetBaseCurrency,
  partnerNames,
  onSaveNames,
  settings,
  onUpdateSettings,
  onResetAll
}: SettingsPageProps) {
  const [draftNames, setDraftNames] = useState<[string, string]>(partnerNames);
  const [isSaved, setIsSaved] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [activeTab, setActiveTab] = useState<'household' | 'projections' | 'exchange'>('household');
  const handleSave = () => {
    const name1 = draftNames[0].trim() || 'Partner 1';
    const name2 = draftNames[1].trim() || 'Partner 2';
    onSaveNames([name1, name2]);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };
  const updateSetting = <K extends keyof Settings,>(key: K, value: Settings[K]) => {
    onUpdateSettings({
      ...settings,
      [key]: value
    });
  };
  return <div className="flex flex-col md:flex-row gap-4 md:gap-6 min-h-[calc(100vh-8rem)]" data-id="element-1751">
      {/* Horizontal tabs on mobile, vertical sidebar on desktop */}
      <div className="md:w-48 md:shrink-0" data-id="element-1752">
        <div className="flex md:flex-col md:sticky md:top-20 gap-1 overflow-x-auto pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide" data-id="element-1753">
          {[{
          key: 'household' as const,
          label: 'Household',
          icon: Users
        }, {
          key: 'projections' as const,
          label: 'Projections',
          icon: SlidersHorizontal
        }, {
          key: 'exchange' as const,
          label: 'Exchange Rates',
          icon: ArrowLeftRight
        }].map(tab => {
          const Icon = tab.icon;
          return <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-2 whitespace-nowrap py-2 px-3 text-sm font-medium rounded-lg transition-all text-left ${activeTab === tab.key ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'}`} data-id="element-1754">
                <Icon className="w-4 h-4 shrink-0" data-id="element-1755" />
                {tab.label}
              </button>;
        })}
        </div>
      </div>

      {/* Content Area — full width */}
      <div className="flex-1 min-w-0" data-id="element-1756">
        <div className="animate-in fade-in duration-300 space-y-8" data-id="element-1757">
          {activeTab === 'household' && <>
              <Card data-id="element-1758">
                <CardHeader title="Household Settings" subtitle="Manage partner names and display preferences" data-id="element-1759" />
                <CardContent className="space-y-6" data-id="element-1760">
                  {/* Base Currency */}
                  <div data-id="element-1761">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1762">
                      <div className="bg-blue-100 p-2 rounded-lg" data-id="element-1763">
                        <Globe className="w-5 h-5 text-blue-600" data-id="element-1764" />
                      </div>
                      <div data-id="element-1765">
                        <h4 className="text-base font-semibold text-slate-800" data-id="element-1766">
                          Base Currency
                        </h4>
                        <p className="text-xs text-slate-500" data-id="element-1767">
                          All holdings and projections are converted to this
                          currency
                        </p>
                      </div>
                    </div>
                    <div className="max-w-xs" data-id="element-1768">
                      <Select searchable value={baseCurrency} onChange={e => onSetBaseCurrency(e.target.value as Currency)} options={CURRENCIES.map(c => ({
                    value: c.code,
                    label: `${c.flag} ${c.code} — ${c.name}`
                  }))} data-id="element-1769" />
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6" data-id="element-1770">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1771">
                      <div className="bg-emerald-100 p-2 rounded-lg" data-id="element-1772">
                        <MapPin className="w-5 h-5 text-emerald-600" data-id="element-1773" />
                      </div>
                      <div data-id="element-1774">
                        <h4 className="text-base font-semibold text-slate-800" data-id="element-1775">
                          Base Country
                        </h4>
                        <p className="text-xs text-slate-500" data-id="element-1776">
                          Default country pre-selected when adding new holdings
                          and properties
                        </p>
                      </div>
                    </div>
                    <div className="max-w-xs" data-id="element-1777">
                      <Select searchable value={settings.baseCountry || 'US'} onChange={e => onUpdateSettings({
                    ...settings,
                    baseCountry: e.target.value
                  })} options={COUNTRIES.map(c => ({
                    value: c.code,
                    label: `${c.flag} ${c.name}`
                  }))} data-id="element-1778" />
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6" data-id="element-1779">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1780">
                      <div className="bg-indigo-100 p-2 rounded-lg" data-id="element-1781">
                        <Users className="w-5 h-5 text-indigo-600" data-id="element-1782" />
                      </div>
                      <h4 className="text-base font-semibold text-slate-800" data-id="element-1783">
                        Partner Names
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl" data-id="element-1784">
                      <div data-id="element-1785">
                        <Label data-id="element-1786">Partner 1</Label>
                        <Input value={draftNames[0]} onChange={e => setDraftNames([e.target.value, draftNames[1]])} placeholder="e.g. Alice" data-id="element-1787" />
                      </div>
                      <div data-id="element-1788">
                        <Label data-id="element-1789">Partner 2</Label>
                        <Input value={draftNames[1]} onChange={e => setDraftNames([draftNames[0], e.target.value])} placeholder="e.g. Bob" data-id="element-1790" />
                      </div>
                    </div>

                    <div className="mt-4 flex items-center gap-3" data-id="element-1791">
                      <Button onClick={handleSave} data-id="element-1792">Save Names</Button>
                      {isSaved && <span className="text-sm text-emerald-600 flex items-center gap-1 animate-in fade-in" data-id="element-1793">
                          <Check className="w-4 h-4" data-id="element-1794" /> Saved successfully
                        </span>}
                    </div>
                    <p className="text-sm text-slate-500 mt-3" data-id="element-1795">
                      Updating names will automatically update all existing
                      holdings assigned to the previous names. Joint holdings
                      will remain unaffected.
                    </p>
                  </div>

                  <div className="border-t border-slate-100 pt-6" data-id="element-1796">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1797">
                      <div className="bg-rose-100 p-2 rounded-lg" data-id="element-1798">
                        <Cake className="w-5 h-5 text-rose-600" data-id="element-1799" />
                      </div>
                      <div data-id="element-1800">
                        <h4 className="text-base font-semibold text-slate-800" data-id="element-1801">
                          Dates of Birth
                        </h4>
                        <p className="text-xs text-slate-500" data-id="element-1802">
                          Used to show ages at retirement and key milestones
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl" data-id="element-1803">
                      <div data-id="element-1804">
                        <Label data-id="element-1805">{partnerNames[0]}'s Date of Birth</Label>
                        <Input type="date" value={settings.partnerDOBs?.[0] || ''} onChange={e => onUpdateSettings({
                      ...settings,
                      partnerDOBs: [e.target.value, settings.partnerDOBs?.[1] || '']
                    })} max={new Date().toISOString().split('T')[0]} data-id="element-1806" />
                        {settings.partnerDOBs?.[0] && <p className="text-xs text-slate-400 mt-1" data-id="element-1807">
                            Currently{' '}
                            {Math.floor((Date.now() - new Date(settings.partnerDOBs[0]).getTime()) / (365.25 * 24 * 60 * 60 * 1000))}{' '}
                            years old
                          </p>}
                      </div>
                      <div data-id="element-1808">
                        <Label data-id="element-1809">{partnerNames[1]}'s Date of Birth</Label>
                        <Input type="date" value={settings.partnerDOBs?.[1] || ''} onChange={e => onUpdateSettings({
                      ...settings,
                      partnerDOBs: [settings.partnerDOBs?.[0] || '', e.target.value]
                    })} max={new Date().toISOString().split('T')[0]} data-id="element-1810" />
                        {settings.partnerDOBs?.[1] && <p className="text-xs text-slate-400 mt-1" data-id="element-1811">
                            Currently{' '}
                            {Math.floor((Date.now() - new Date(settings.partnerDOBs[1]).getTime()) / (365.25 * 24 * 60 * 60 * 1000))}{' '}
                            years old
                          </p>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Danger Zone */}
              <div className="rounded-xl border-2 border-red-200 bg-red-50/30 overflow-hidden" data-id="element-1812">
                <div className="px-6 py-4 border-b border-red-200 bg-red-50/50" data-id="element-1813">
                  <div className="flex items-center gap-2" data-id="element-1814">
                    <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-1815" />
                    <h3 className="text-base font-semibold text-red-900" data-id="element-1816">
                      Danger Zone
                    </h3>
                  </div>
                </div>
                <div className="px-6 py-5" data-id="element-1817">
                  <div className="flex items-start justify-between gap-6" data-id="element-1818">
                    <div data-id="element-1819">
                      <h4 className="text-sm font-semibold text-slate-900" data-id="element-1820">
                        Reset All Application Data
                      </h4>
                      <p className="text-sm text-slate-500 mt-1" data-id="element-1821">
                        Permanently delete all holdings, loans, cash events,
                        snapshots, settings, and partner names. This action
                        cannot be undone.
                      </p>
                    </div>
                    <Button variant="secondary" onClick={() => {
                  setShowResetConfirm(true);
                  setResetConfirmText('');
                }} className="shrink-0 text-red-600 border-red-300 hover:bg-red-100 hover:border-red-400" data-id="element-1822">
                      <Trash2 className="w-4 h-4 mr-2" data-id="element-1823" />
                      Reset Everything
                    </Button>
                  </div>

                  {showResetConfirm && <div className="mt-4 pt-4 border-t border-red-200 max-w-md" data-id="element-1824">
                      <Label className="text-red-800" data-id="element-1825">
                        Type{' '}
                        <span className="font-mono font-bold bg-red-100 px-1.5 py-0.5 rounded" data-id="element-1826">
                          RESET
                        </span>{' '}
                        to confirm
                      </Label>
                      <Input value={resetConfirmText} onChange={e => setResetConfirmText(e.target.value)} placeholder="Type RESET here" className="max-w-xs border-red-300 focus:border-red-500 focus:ring-red-500" autoFocus data-id="element-1827" />
                      <div className="mt-3 flex items-center gap-3" data-id="element-1828">
                        <button disabled={resetConfirmText !== 'RESET'} onClick={() => {
                    onResetAll();
                    setShowResetConfirm(false);
                    setResetConfirmText('');
                  }} className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all ${resetConfirmText === 'RESET' ? 'bg-red-600 hover:bg-red-700 cursor-pointer' : 'bg-red-300 cursor-not-allowed'}`} data-id="element-1829">
                          <span className="flex items-center gap-2" data-id="element-1830">
                            <Trash2 className="w-4 h-4" data-id="element-1831" />
                            Confirm Reset
                          </span>
                        </button>
                        <button onClick={() => {
                    setShowResetConfirm(false);
                    setResetConfirmText('');
                  }} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition-colors" data-id="element-1832">
                          Cancel
                        </button>
                      </div>
                    </div>}
                </div>
              </div>
            </>}

          {activeTab === 'projections' && <>
              <Card data-id="element-1833">
                <CardHeader title="Planning Assumptions" subtitle="Return rates, contributions, and time horizon used across all projections" data-id="element-1834" />
                <CardContent className="space-y-6" data-id="element-1835">
                  <div data-id="element-1836">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1837">
                      <div className="bg-emerald-100 p-2 rounded-lg" data-id="element-1838">
                        <TrendingUp className="w-5 h-5 text-emerald-600" data-id="element-1839" />
                      </div>
                      <h4 className="text-base font-semibold text-slate-800" data-id="element-1840">
                        Contributions & Timeline
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl" data-id="element-1841">
                      <div data-id="element-1842">
                        <Label data-id="element-1843">
                          Monthly Contribution ({baseCurrency})
                          <InfoTooltip text="Amount invested each month during accumulation. Added to liquid holdings. Stops at retirement." data-id="element-1844" />
                        </Label>
                        <Input type="number" min="0" step="100" value={settings.monthlyContribution} onChange={e => updateSetting('monthlyContribution', Number(e.target.value))} data-id="element-1845" />
                      </div>
                      <div data-id="element-1846">
                        <Label data-id="element-1847">
                          Time Horizon (years)
                          <InfoTooltip text="Total simulation window. The projection shows accumulation + drawdown within this period." data-id="element-1848" />
                        </Label>
                        <div className="flex items-center gap-3" data-id="element-1849">
                          <input type="range" min="1" max="60" step="1" value={settings.timeHorizon} onChange={e => updateSetting('timeHorizon', Number(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600" data-id="element-1850" />
                          <span className="font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded text-sm w-14 text-center shrink-0" data-id="element-1851">
                            {settings.timeHorizon}y
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6" data-id="element-1852">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1853">
                      <div className="bg-rose-100 p-2 rounded-lg" data-id="element-1854">
                        <TrendingUp className="w-5 h-5 text-rose-600" data-id="element-1855" />
                      </div>
                      <div data-id="element-1856">
                        <h4 className="text-base font-semibold text-slate-800" data-id="element-1857">
                          Inflation Adjustment
                        </h4>
                        <p className="text-xs text-slate-500" data-id="element-1858">
                          Increase retirement withdrawals annually to maintain
                          purchasing power
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3 max-w-2xl" data-id="element-1859">
                      <label className="flex items-center gap-3 cursor-pointer" data-id="element-1860">
                        <input type="checkbox" checked={settings.inflationEnabled ?? false} onChange={e => updateSetting('inflationEnabled', e.target.checked)} className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500" data-id="element-1861" />
                        <span className="text-sm text-slate-700" data-id="element-1862">
                          Enable inflation-adjusted drawdowns
                        </span>
                      </label>
                      {(settings.inflationEnabled ?? false) && <div data-id="element-1863">
                          <Label data-id="element-1864">
                            Annual Inflation Rate (%)
                            <InfoTooltip text="Your monthly drawdown will increase by this percentage each year of retirement. E.g., at 3% inflation, a 10,000/mo drawdown becomes 10,300/mo in year 2, 10,609/mo in year 3, etc." data-id="element-1865" />
                          </Label>
                          <div className="flex items-center gap-3" data-id="element-1866">
                            <input type="range" min="0.5" max="10" step="0.5" value={settings.inflationRate ?? 3} onChange={e => updateSetting('inflationRate', Number(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-rose-500" data-id="element-1867" />
                            <span className="font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded text-sm w-14 text-center shrink-0" data-id="element-1868">
                              {settings.inflationRate ?? 3}%
                            </span>
                          </div>
                        </div>}
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6" data-id="element-1869">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1870">
                      <div className="bg-amber-100 p-2 rounded-lg" data-id="element-1871">
                        <Flame className="w-5 h-5 text-amber-600" data-id="element-1872" />
                      </div>
                      <h4 className="text-base font-semibold text-slate-800" data-id="element-1873">
                        FIRE Target
                      </h4>
                    </div>
                    <div className="max-w-xs" data-id="element-1874">
                      <Label data-id="element-1875">
                        Desired Monthly Income ({baseCurrency})
                        <InfoTooltip text="How much you want to withdraw each month in retirement." data-id="element-1876" />
                      </Label>
                      <Input type="number" min="0" step="100" value={settings.desiredDrawdown} onChange={e => updateSetting('desiredDrawdown', Number(e.target.value))} data-id="element-1877" />
                      {settings.desiredDrawdown > 0 && <p className="text-xs text-slate-400 mt-1.5" data-id="element-1878">
                          ={' '}
                          {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: baseCurrency,
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }).format(settings.desiredDrawdown * 12)}
                          /year
                        </p>}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-1879">
                <CardHeader title="Retirement Strategy" subtitle="Configure how your FIRE calculations are performed" data-id="element-1880" />
                <CardContent className="space-y-6" data-id="element-1881">
                  <div data-id="element-1882">
                    <div className="flex items-center gap-2 mb-4" data-id="element-1883">
                      <div className="bg-amber-100 p-2 rounded-lg" data-id="element-1884">
                        <Flame className="w-5 h-5 text-amber-600" data-id="element-1885" />
                      </div>
                      <h4 className="text-base font-semibold text-slate-800" data-id="element-1886">
                        Withdrawal Strategy
                      </h4>
                    </div>

                    <div className="space-y-4 max-w-2xl" data-id="element-1887">
                      <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${settings.withdrawalStrategy === 'drawdown' ? 'border-amber-500 bg-amber-50/50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1888">
                        <input type="radio" name="withdrawalStrategy" value="drawdown" checked={settings.withdrawalStrategy === 'drawdown'} onChange={() => onUpdateSettings({
                      ...settings,
                      withdrawalStrategy: 'drawdown'
                    })} className="mt-1 w-4 h-4 text-amber-600 focus:ring-amber-500" data-id="element-1889" />
                        <div data-id="element-1890">
                          <p className="font-semibold text-slate-800" data-id="element-1891">
                            Principal Drawdown (e.g., 4% Rule)
                          </p>
                          <p className="text-sm text-slate-500 mt-1" data-id="element-1892">
                            You gradually spend down your portfolio over your
                            time horizon. Results in a smaller FIRE target, but
                            your portfolio will eventually deplete.
                          </p>
                        </div>
                      </label>

                      <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${settings.withdrawalStrategy === 'interest-only' ? 'border-amber-500 bg-amber-50/50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1893">
                        <input type="radio" name="withdrawalStrategy" value="interest-only" checked={settings.withdrawalStrategy === 'interest-only'} onChange={() => onUpdateSettings({
                      ...settings,
                      withdrawalStrategy: 'interest-only'
                    })} className="mt-1 w-4 h-4 text-amber-600 focus:ring-amber-500" data-id="element-1894" />
                        <div data-id="element-1895">
                          <p className="font-semibold text-slate-800" data-id="element-1896">
                            Interest Only
                          </p>
                          <p className="text-sm text-slate-500 mt-1" data-id="element-1897">
                            You never touch the principal and only spend the
                            returns. Results in a larger FIRE target, but your
                            portfolio lasts indefinitely.
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>}

          {activeTab === 'exchange' && <>
              <section data-id="element-1898">
                <ExchangeRateWidget baseCurrency={baseCurrency} data-id="element-1899" />
              </section>
            </>}
        </div>
      </div>
    </div>;
}